import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { recruitmentApi } from "../api/recruitment";
import type { Candidate } from "../types";

const QK = "candidates";

export function useCandidates(params?: Record<string, string>) {
  return useQuery({
    queryKey: [QK, params],
    queryFn: () => recruitmentApi.list(params),
    staleTime: 0,
  });
}

export function useCreateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Candidate>) => recruitmentApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useUpdateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Candidate> }) =>
      recruitmentApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useDeleteCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => recruitmentApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useUploadCandidateFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      candidateId,
      file,
      fileType,
    }: {
      candidateId: string;
      file: File;
      fileType: string;
    }) => recruitmentApi.uploadFile(candidateId, file, fileType),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useTodayInterviewsCount(enabled = true) {
  return useQuery({
    queryKey: ["today-interviews-count"],
    queryFn: () => recruitmentApi.todayInterviewsCount(),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
}

export function useCandidateSmsInfo(candidateId?: string) {
  return useQuery({
    queryKey: ["candidate-sms", candidateId],
    queryFn: () => recruitmentApi.getSmsInfo(candidateId!),
    enabled: !!candidateId,
    staleTime: 0,
  });
}

export function useSendInterviewSms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      candidateId,
      message,
    }: {
      candidateId: string;
      message?: string;
    }) => recruitmentApi.sendInterviewSms(candidateId, message),
    onSuccess: (_data, { candidateId }) =>
      qc.invalidateQueries({ queryKey: ["candidate-sms", candidateId] }),
  });
}

export function useDeleteCandidateFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      candidateId,
      fileId,
    }: {
      candidateId: string;
      fileId: string;
    }) => recruitmentApi.deleteFile(candidateId, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}
